import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Slider,
} from "@mui/material";
import { Delete } from "@mui/icons-material";
import TimeCard from "../common/TimeCard";
import { Shop } from "../../types/shop.types";
import { Employee } from "../../types/employee.types";
import { WorkingHours } from "../../types/schedule.types";
import { calculateEndTime } from "../../utils/scheduleHelpers";

interface FormValidation {
  isValid: boolean;
  startTimeError?: string;
  durationError?: string;
}

interface SchedulesDialogProps {
  open: boolean;
  onClose: () => void;
  // Context
  selectedShop: Shop | null;
  selectedEmployee: Employee | null;
  selectedDate: Date | null;
  selectedWorkingHours: WorkingHours | null;
  // Form state
  startTime: string;
  duration: number;
  onStartTimeChange: (time: string) => void;
  onDurationChange: (duration: number) => void;
  // Validation
  formValidation: FormValidation;
  // Actions
  onSave: () => void;
  onDelete: () => void;
  // Helper data
  availableHours: number;
}

export default function SchedulesDialog({
  open,
  onClose,
  selectedShop,
  selectedEmployee,
  selectedDate,
  selectedWorkingHours,
  startTime,
  duration,
  onStartTimeChange,
  onDurationChange,
  formValidation,
  onSave,
  onDelete,
  availableHours,
}: SchedulesDialogProps) {
  const isEditing = !!selectedWorkingHours;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }} component="div">
        <Typography variant="h6" component="h2" fontWeight={600}>
          {isEditing ? "Edit Working Hours" : "Add Working Hours"}
        </Typography>
        {selectedEmployee && (
          <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="body1" fontWeight={600} color="primary">
              {selectedEmployee.name}
            </Typography>
            {selectedDate && selectedShop && (
              <>
                <Typography variant="body2" color="text.secondary">
                  •
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedShop.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  •
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedDate.toLocaleDateString("en-US", {
                    weekday: "short",
                    month: "short",
                    day: "numeric",
                  })}
                </Typography>
              </>
            )}
          </Box>
        )}
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3, mt: 1 }}>
          <Box>
            <Typography
              variant="caption"
              fontWeight={600}
              color="text.secondary"
              sx={{ mb: 1, display: "block" }}
            >
              START TIME
            </Typography>
            <TimeCard
              label="Start Time"
              value={startTime}
              onChange={onStartTimeChange}
              step={30}
              color={formValidation.startTimeError ? "error" : "primary"}
            />
            {formValidation.startTimeError && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
                {formValidation.startTimeError}
              </Typography>
            )}
          </Box>
          <Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Duration
              </Typography>
              <Typography variant="body1" fontWeight={600} color="primary">
                {duration}h
                {selectedEmployee && selectedShop && selectedDate && (
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                    (ends {calculateEndTime(startTime, duration)})
                  </Typography>
                )}
              </Typography>
            </Box>
            <Slider
              value={duration}
              onChange={(_, value) => onDurationChange(value as number)}
              min={0.5}
              max={Math.max(availableHours, 0.5)}
              step={0.5}
              marks={[
                { value: 0.5, label: "0.5h" },
                { value: Math.max(availableHours, 0.5) / 2, label: `${(Math.max(availableHours, 0.5) / 2).toFixed(1)}h` },
                { value: Math.max(availableHours, 0.5), label: `${Math.max(availableHours, 0.5).toFixed(1)}h` },
              ]}
              valueLabelDisplay="auto"
              valueLabelFormat={(value) => `${value}h`}
              sx={{
                "& .MuiSlider-markLabel": {
                  fontSize: "0.75rem",
                },
              }}
            />
            {formValidation.durationError && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
                {formValidation.durationError}
              </Typography>
            )}
          </Box>
          {selectedEmployee && selectedShop && selectedDate && (
            <Box
              sx={{
                p: 2,
                bgcolor: "grey.50",
                borderRadius: 1,
                border: 1,
                borderColor: "grey.200",
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 0.5 }}
              >
                Summary
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Available Hours:
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {availableHours.toFixed(1)}h
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        {isEditing && (
          <Button
            onClick={onDelete}
            color="error"
            startIcon={<Delete />}
            sx={{ mr: "auto" }}
          >
            Remove
          </Button>
        )}
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={onSave}
          variant="contained"
          disabled={!formValidation.isValid}
          sx={{ minWidth: 100 }}
        >
          {isEditing ? "Update" : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
